/* eslint-disable import/no-internal-modules */
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ProjectId } from 'api/models';
import { RootState } from 'data';
import { useAddTeamsToProjectMutation } from 'queries/project';
import { Content } from 'stores/core';
import { Memberships, MembershipsStore } from 'stores/memberships';
import { Option } from '../squad-picker/types';

interface ReturnProps {
  addTeamsToProject: (projectId: ProjectId, selected: Option[]) => void;
}

export const useAssignTeamsToProject = (): ReturnProps => {
  const { mutate: addTeamsToProjectMutation } = useAddTeamsToProjectMutation();
  const membershipsStore: MembershipsStore = useSelector(
    (store: RootState) => store.memberships,
  );
  const getTeamIdsInProject = useCallback(
    (projectId: ProjectId) =>
      (projectId != null
        ? Content.orElse(Memberships.empty())(
            MembershipsStore.ofProject(projectId)(membershipsStore),
          )?.placeholders ?? []
        : []
      ).map(({ teamId }) => teamId),
    [membershipsStore],
  );

  const isTeamAlreadyInProject = useCallback(
    (projectId: ProjectId, teamId: number) =>
      getTeamIdsInProject(projectId).includes(teamId),
    [getTeamIdsInProject],
  );

  const addTeamsToProject = useCallback(
    (projectId: ProjectId, selected: Option[]) => {
      const selectedDefaultTeamIds = selected
        ?.filter(
          (item) =>
            item.extra.type === 'team' &&
            !isTeamAlreadyInProject(projectId, item.extra.id),
        )
        .map(({ extra: { id } }) => id);

      if (
        selectedDefaultTeamIds != null &&
        selectedDefaultTeamIds?.length > 0
      ) {
        const [, _projectId] = projectId;

        addTeamsToProjectMutation({
          projectId: _projectId,
          teamIds: selectedDefaultTeamIds,
        });
      }
    },
    [addTeamsToProjectMutation, isTeamAlreadyInProject],
  );

  return {
    addTeamsToProject,
  };
};
